import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/UserRole';

const ERROR_MESSAGES = {
  INVALID_CREDENTIALS: 'E-mail ou senha incorretos. Por favor, verifique suas credenciais e tente novamente.',
  NETWORK_ERROR: 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.',
  SERVER_ERROR: 'Erro no servidor. Por favor, tente novamente mais tarde.',
  INACTIVE_ACCOUNT: 'Sua conta está inativa. Entre em contato com o suporte.',
  INVALID_EMAIL: 'Por favor, insira um endereço de e-mail válido.',
  EMPTY_FIELDS: 'Por favor, preencha todos os campos obrigatórios.',
  UNKNOWN_ERROR: 'Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.',
  TOO_MANY_ATTEMPTS:
    'Muitas tentativas de login falharam. Aguarde alguns minutos antes de tentar novamente.',
} as const;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    const saved = localStorage.getItem('rememberMe');
    return saved === 'true';
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (isAuthenticated && user) {
      let redirectPath = '/';
      if (user.role === UserRole.ADMIN) {
        redirectPath = '/admin/dashboard';
      } else if (user.role === UserRole.CLIENT) {
        redirectPath = '/client/dashboard';
      } else if (user.role === UserRole.USER) {
        redirectPath = '/user/historico-viagens';
      }

      if (location.pathname !== redirectPath) {
        navigate(redirectPath, { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, location.pathname]);

  useEffect(() => {
    localStorage.setItem('rememberMe', rememberMe.toString());
  }, [rememberMe]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const normalizedEmail = email.trim();
    const normalizedPassword = password.trim();

    if (!normalizedEmail || !normalizedPassword) {
      setError(ERROR_MESSAGES.EMPTY_FIELDS);
      setLoading(false);
      return;
    }

    if (!validateEmail(normalizedEmail)) {
      setError(ERROR_MESSAGES.INVALID_EMAIL);
      setLoading(false);
      return;
    }

    try {
      const result = await login(normalizedEmail, normalizedPassword);

      await new Promise(resolve => setTimeout(resolve, 100));

      let userRole = null;
      if (result) {
        userRole = result.role;
      }

      let redirectPath = '/login';
      if (userRole === UserRole.ADMIN) {
        redirectPath = '/admin/dashboard';
      } else if (userRole === UserRole.CLIENT) {
        redirectPath = '/client/dashboard';
      } else if (userRole === UserRole.USER) {
        redirectPath = '/user/historico-viagens';
      }

      navigate(redirectPath, { replace: true });
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = err as any;
      if (error?.name === 'ApiError' || (error?.statusCode !== undefined)) {
        if (error.statusCode === 401) {
          setError(ERROR_MESSAGES.INVALID_CREDENTIALS);
        } else if (error.statusCode === 429) {
          setError(ERROR_MESSAGES.TOO_MANY_ATTEMPTS);
        } else if (error.message) {
          setError(error.message);
        } else {
          setError(ERROR_MESSAGES.SERVER_ERROR);
        }
      } else if (error.response) {
        if (error.response.status === 401) {
          setError(ERROR_MESSAGES.INVALID_CREDENTIALS);
        } else if (error.response.status === 429) {
          setError(ERROR_MESSAGES.TOO_MANY_ATTEMPTS);
        } else if (error.response.data?.message) {
          setError(error.response.data.message);
        } else {
          setError(ERROR_MESSAGES.SERVER_ERROR);
        }
      } else if (error.request) {
        setError(ERROR_MESSAGES.NETWORK_ERROR);
      } else {
        setError(ERROR_MESSAGES.UNKNOWN_ERROR);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 sm:p-6 bg-gradient-to-br from-slate-200 via-blue-100 to-slate-300">
      <section className="w-full max-w-md rounded-2xl border border-slate-300 bg-white p-7 sm:p-9 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25),0_0_0_1px_rgba(255,255,255,0.6)_inset]">
        <header className="mb-8 text-center">
          <img
            src="/suafrota_dasilva.png"
            alt="SuaFrota"
            className="mx-auto mb-7 h-36 w-auto max-w-[360px] object-contain"
          />
          <h1 className="text-[24px] font-bold tracking-tight text-slate-900">
            Entrar no painel
          </h1>
          <p className="mt-1.5 text-[14px] font-medium text-slate-500">
            Acesse sua conta da SuaFrota
          </p>
        </header>

        {error && (
          <div
            role="alert"
            className="mb-5 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3.5"
          >
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 stroke-[1.9] text-rose-600" />
            <p className="text-[13px] leading-relaxed font-medium text-rose-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <div>
            <label htmlFor="email" className="sr-only">
              E-mail
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                <Mail className="h-[18px] w-[18px] stroke-[1.8]" />
              </div>
              <input
                id="email"
                type="email"
                placeholder="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="block w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-3.5 text-[14px] text-slate-900 placeholder:text-slate-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/15 disabled:bg-slate-50 disabled:text-slate-500"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="sr-only">
              Senha
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                <Lock className="h-[18px] w-[18px] stroke-[1.8]" />
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="block w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-12 text-[14px] text-slate-900 placeholder:text-slate-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/15 disabled:bg-slate-50 disabled:text-slate-500"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 transition-colors hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 rounded-lg"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? (
                  <EyeOff className="h-[18px] w-[18px] stroke-[1.8]" />
                ) : (
                  <Eye className="h-[18px] w-[18px] stroke-[1.8]" />
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-0.5">
            <label htmlFor="remember-me" className="inline-flex cursor-pointer select-none items-center gap-2.5">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded-md border-slate-300 text-blue-600 shadow-sm focus:ring-2 focus:ring-blue-500/30 focus:ring-offset-0"
                disabled={loading}
              />
              <span className="text-[13px] font-medium text-slate-600">
                Lembrar de mim
              </span>
            </label>
            <div className="text-right">
              <p className="text-[12px] font-medium text-slate-500">
                Precisa resetar sua senha?
              </p>
              <p className="text-[12px] font-semibold text-slate-500">
                Contate o administrador.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-blue-600 via-blue-600 to-blue-700 px-5 text-[14px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)] transition-all duration-200 hover:from-blue-700 hover:via-blue-700 hover:to-blue-800 hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.65)] focus:outline-none focus:ring-4 focus:ring-blue-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none"
          >
            {loading ? (
              <>
                <div className="h-[18px] w-[18px] animate-spin rounded-full border-[2.5px] border-white/40 border-t-white" />
                <span className="font-semibold tracking-wide">Entrando...</span>
              </>
            ) : (
              <span className="font-semibold tracking-wide">Entrar</span>
            )}
          </button>

          <p className="mt-6 text-center text-[13px] leading-relaxed font-medium text-slate-500">
            O acesso ao sistema é liberado pelo administrador na tela de{' '}
            <span className="font-semibold text-slate-700">Funcionários</span>.
          </p>
        </form>
      </section>
    </div>
  );
};

export default Login;
